import { useState } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { addSelectedTag, removeSelectedTag, setSelectedTags } from '@/store/slices/uiSlice';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tag, ChevronDown, X, Search } from 'lucide-react';

interface TagsFilterProps {
  tags: string[];
}

export function TagsFilter({ tags }: TagsFilterProps) {
  const dispatch = useAppDispatch();
  const { selectedTags } = useAppSelector((state) => state.ui.fileFilters);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredTags = tags.filter(
    (tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()) && !selectedTags.includes(tag)
  );

  const handleTagToggle = (tag: string) => {
    if (selectedTags.includes(tag)) {
      dispatch(removeSelectedTag(tag));
    } else {
      dispatch(addSelectedTag(tag));
    }
  };

  const clearAllTags = () => {
    dispatch(setSelectedTags([]));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-2">
          <Tag className="h-3 w-3" />
          <span className="hidden sm:inline">Tags</span>
          {selectedTags.length > 0 && (
            <Badge variant="secondary" className="ml-1 h-4 px-1 text-xs">
              {selectedTags.length}
            </Badge>
          )}
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {/* Search Input */}
        <div className="p-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>

        {/* Selected Tags */}
        {selectedTags.length > 0 && (
          <>
            <div className="px-2 pb-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground">
                  Selected ({selectedTags.length})
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllTags}
                  className="h-6 px-2 text-xs"
                >
                  Clear all
                </Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {selectedTags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs gap-1 pr-1">
                    <span>{tag}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => dispatch(removeSelectedTag(tag))}
                      className="h-3 w-3 p-0 hover:bg-destructive hover:text-destructive-foreground"
                    >
                      <X className="h-2 w-2" />
                    </Button>
                  </Badge>
                ))}
              </div>
            </div>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Available Tags */}
        <ScrollArea className="max-h-40">
          <div className="p-1">
            {filteredTags.length > 0 ? (
              filteredTags.map((tag) => (
                <DropdownMenuItem
                  key={tag}
                  onClick={() => handleTagToggle(tag)}
                  className="flex items-center justify-between cursor-pointer"
                >
                  <span>{tag}</span>
                  <Badge variant="outline" className="text-xs">
                    {tags.filter((t) => t === tag).length}
                  </Badge>
                </DropdownMenuItem>
              ))
            ) : (
              <div className="px-2 py-3 text-center text-sm text-muted-foreground">
                {searchTerm ? (
                  <span>No tags match "{searchTerm}"</span>
                ) : (
                  <span>All tags selected</span>
                )}
              </div>
            )}
          </div>
        </ScrollArea>

        {tags.length === 0 && (
          <div className="px-2 py-3 text-center text-sm text-muted-foreground">No tags found</div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
